import { Color } from "deck.gl";
import { memo } from "react";
import { Button } from "react-bootstrap";
import { Trash } from "react-bootstrap-icons";
import { RowComponentProps } from "react-window";

export const MEAN_DX                  = -2.61;
export const MEAN_DY                  = 12.27;
export const MAX_DEV                  = 2.0;
export const lineColor:         Color = [0,   200, 0,   200];
export const selectedLineColor: Color = [0,   255, 255, 255];
export const filledLineColor:   Color = [100, 100, 100, 255];
export const RECT_SIZE = 16;
export const NUDGE     = 0.25;
export type PairSrc = "user" | "loftr" | "rbf";

export interface RectPair {
    id:         number;
    bx:         number;
    by:         number;
    ax:         number;
    ay:         number;
    matchCount: number;
    score:      number | null;
    status?:    RectStatus;
    src:        PairSrc;
}

export enum RectStatus {
    New  = "New",
    Done = "Done"
}

interface PairRowProps {
    pairs:       RectPair[];
    selectedIds: Set<number>;
    onSelect:    ( pair: RectPair ) => void;
    onRemove:    ( id:   number   ) => void;
}

export const PairRow = ( { index, pairs, selectedIds, onSelect, onRemove, style }: RowComponentProps<PairRowProps> ) => {
    const pair       = pairs[index];
    const isSelected = selectedIds.has( pair.id );
    const isDone     = pair.status === RectStatus.Done;
    return (
        <div style={{ 
            ...style,
            display:             "grid", 
            gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
            cursor:              "pointer",
            alignItems:          "center" }}
            className={`p-1 mx-1 border shadow small rounded ${isSelected ? "bg-primary text-white" : "" }`} 
            onClick={ () => onSelect( pair ) }>
            <div className="d-flex justify-content-center">#{pair.id}</div>
            <div>x{( pair.bx - pair.ax ).toFixed( 2 )}</div>
            <div>y{( pair.by - pair.ay ).toFixed( 2 )}</div>
            <div>{((pair.score ?? 0) * 100).toFixed()}%</div>
            <div>{pair.status}</div>
            <Button variant="danger" className="p-1"
                onClick={ ( e ) => { e.stopPropagation(); onRemove( pair.id ); } }
                aria-label="Remove">
                    <Trash size={20} />
            </Button>
        </div>
    );
};

export const MemoPairRow = memo( PairRow );

export function storageKey(aId: number, bId: number) {
    return `image-align:${aId}:${bId}`;
}

export function loadPairs(aId: number, bId: number): { pairs: RectPair[]; nextId: number } {
    try {
        const raw = localStorage.getItem( storageKey( aId, bId ) );
        if( raw ) {
            const pairs: RectPair[] = JSON.parse( raw );
            const maxId = pairs.reduce( ( m, p ) => Math.max( m, p.id ), 0 );
            return { pairs, nextId: maxId + 1 };
        }
    } catch {
        /* ignore */
    }
    return { pairs: [], nextId: 1 };
}

export function savePairs(aId: number, bId: number, pairs: RectPair[]) {
    localStorage.setItem(storageKey(aId, bId), JSON.stringify(pairs));
}

export function makeRectPolygon(cx: number, cy: number, half: number) {
    const l = cx - half, r = cx + half, t = cy - half, b = cy + half;
    return [[l, t], [r, t], [r, b], [l, b], [l, t]];
}

export function alpha( col: Color, alpha: number ) {
    const [r, g, b, _] = Array.from( col );
    return [r, g, b, alpha];
}